import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { BellRing, Check, Radio } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { getNewLookTokens } from "common/NewLook/tokens";
import { ONBOARDING_PHONE_GUTTER } from "features/onboarding/common/onboardingUi";
import { createChart } from "features/onboarding/visuals/live/chart";
import {
  axisWidth,
  chartHeight,
  geometry,
} from "features/onboarding/visuals/live/geometry";
import { STORIES, Tr } from "features/onboarding/visuals/live/stories";
import {
  clamp01,
  FADE_IN,
  FADE_OUT,
  Story,
} from "features/onboarding/visuals/live/story";
import { useLiveClock } from "features/onboarding/visuals/live/useLiveClock";
import type {
  SceneRuntime,
  VisualMethod,
} from "features/onboarding/visuals/types";

const PHONE_QUERY = "(max-width: 639px)";
/** The walkthrough's phone layout (WalkthroughLayout's PHONE). */
const WALKTHROUGH_PHONE = 560;

/**
 * A bot type's walkthrough illustration: a card with the pair, a status and
 * one or two counters, the signal or alert strip where the method has one,
 * and a live, scrolling chart of the story. One clock drives it all.
 */
const LiveCard = ({
  method,
  runtime,
}: {
  method: VisualMethod;
  runtime: SceneRuntime;
}) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  // `t` is a new function on every render; the story only changes with the language.
  const translate = useRef(t);
  translate.current = t;
  const story = useMemo(
    () =>
      STORIES[method]((key, options) =>
        (translate.current as unknown as Tr)(key, options)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [method, i18n.language]
  );
  const phone: boolean = useMedia([PHONE_QUERY], [true], false);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const host = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const status = useRef<HTMLSpanElement>(null);
  const statusText = useRef<HTMLSpanElement>(null);
  const stats = useRef<(HTMLSpanElement | null)[]>([]);
  const strip = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [redraw, setRedraw] = useState(0);
  // Fonts already in (the usual case after the first screen): build once.
  const [fonts, setFonts] = useState(() =>
    document.fonts?.status === "loaded" ? 1 : 0
  );
  // Off screen (scrolled past on a phone) the clock stops on its frame.
  const [visible, setVisible] = useState(true);
  // The strip's items and their ticks, found once per build, not per frame.
  const stripItems = useRef<{ item: HTMLElement; tick: HTMLElement | null }[]>(
    []
  );
  const chart = useRef<ReturnType<typeof createChart> | null>(null);

  useLayoutEffect(() => {
    const node = host.current;
    if (!node) return;
    const measure = () => setWidth(Math.round(node.clientWidth));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!svg.current || width < 120) return;
    const g = geometry(
      story,
      width,
      chartHeight(!!story.strip, phone),
      axisWidth(method !== "grid", phone),
      phone
    );
    chart.current = createChart(svg.current, story, g, uid);
    setRedraw((n) => n + 1);
  }, [story, width, phone, method, uid, fonts]);

  // Fonts change text widths: build again (chips are measured) once they are in.
  useEffect(() => {
    if (fonts) return;
    let live = true;
    document.fonts?.ready.then(() => {
      if (live) setFonts((n) => n + 1);
    });
    return () => {
      live = false;
    };
  }, [fonts]);

  useEffect(() => {
    const node = host.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry.isIntersecting)
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    stripItems.current = Array.from(
      strip.current?.querySelectorAll<HTMLElement>("[data-item]") ?? []
    ).map((item) => ({
      item,
      tick: item.querySelector<HTMLElement>("[data-tick]"),
    }));
  }, [story, redraw]);

  const initialStats = story.stats(0);
  const widestStatus = useMemo(() => {
    let widest = story.status(0).text;
    for (let t = 0; t <= story.loop; t += 0.05) {
      const text = story.status(t).text;
      if (text.length > widest.length) widest = text;
    }
    return widest;
  }, [story]);
  // Each stat's widest value over the loop: it sizes the stat, so the column
  // keeps one width and the pair's text never reflows between steps.
  const widestStats = useMemo(() => {
    const widest = initialStats.map((s) => s.value);
    for (let t = 0; t <= story.loop; t += 0.05)
      story.stats(t).forEach((s, i) => {
        if (s.value.length > (widest[i]?.length ?? 0)) widest[i] = s.value;
      });
    return widest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story]);
  const draw = (time: number) => {
    chart.current?.render(time);
    const now = story.status(time);
    if (statusText.current && statusText.current.textContent !== now.text)
      statusText.current.textContent = now.text;
    status.current?.toggleAttribute("data-closed", !!now.closed);
    story.stats(time).forEach((s, i) => {
      const node = stats.current[i];
      if (!node) return;
      if (node.textContent !== s.value) node.textContent = s.value;
      node.toggleAttribute("data-positive", !!s.positive);
    });
    stripItems.current.forEach(({ item, tick }, i) => {
      const at = story.strip!.at[i];
      const k = clamp01((time - at) / 0.35);
      item.style.opacity = String(k);
      item.style.transform = `translateY(${((1 - k) * 4).toFixed(2)}px)`;
      if (tick) {
        const s = clamp01((time - at - 0.25) / 0.3);
        tick.style.opacity = String(s);
        tick.style.transform = `scale(${(0.4 + 0.6 * s).toFixed(3)})`;
      }
    });
    if (strip.current) strip.current.style.opacity = String(fade(story, time));
  };
  useLiveClock(story, runtime, draw, redraw, !visible);

  const { header } = story;
  return (
    <Card>
      <Head>
        <Pair>
          <Coin style={{ background: header.coin.color }} aria-hidden>
            {header.coin.glyph}
          </Coin>
          <div>
            <PairName>
              {header.pair}
              <Status ref={status}>
                <i aria-hidden />
                {/* The text over its widest one (hidden): the pill keeps one
                    width, so the pair's line never wraps between steps. */}
                <StatusWords>
                  <span ref={statusText}>{story.status(0).text}</span>
                  <span aria-hidden={true}>{widestStatus}</span>
                </StatusWords>
              </Status>
            </PairName>
            <PairMeta>
              {/* It wraps after a "·" only ("Auto-follow" stays whole). */}
              {header.meta.split(" · ").map((part, index, parts) => (
                <Fragment key={index}>
                  <span>
                    {part}
                    {index < parts.length - 1 && " ·"}
                  </span>
                  {index < parts.length - 1 && " "}
                </Fragment>
              ))}
            </PairMeta>
          </div>
        </Pair>
        <Stats>
          {initialStats.map((s, i) => (
            <div key={s.label}>
              <StatLabel>{s.label}</StatLabel>
              <StatValue>
                <span
                  ref={(node) => {
                    stats.current[i] = node;
                  }}
                >
                  {s.value}
                </span>
                <span aria-hidden={true}>{widestStats[i]}</span>
              </StatValue>
            </div>
          ))}
        </Stats>
      </Head>
      {story.strip && (
        <Strip ref={strip}>
          <StripTitle>
            {story.strip.icon === "radio" ? (
              <Radio size={14} strokeWidth={1.9} aria-hidden />
            ) : (
              <BellRing size={14} strokeWidth={1.9} aria-hidden />
            )}
            {story.strip.title}
          </StripTitle>
          <Chips>
            {story.strip.items.map((item, i) => (
              <StripChip key={i} data-item>
                {item.label}
                {item.value && <b>{item.value}</b>}
                {item.tag && <Tag>{item.tag}</Tag>}
                <Tick data-tick aria-hidden>
                  <Check size={9} strokeWidth={3.5} />
                </Tick>
              </StripChip>
            ))}
          </Chips>
        </Strip>
      )}
      <Plot ref={host} $height={chartHeight(!!story.strip, phone)}>
        <svg ref={svg} aria-hidden />
      </Plot>
      <Note>{t("visual.live.note")}</Note>
    </Card>
  );
};

export default LiveCard;

const fade = (story: Story, time: number): number =>
  time > story.loop - FADE_OUT
    ? Math.max(0, (story.loop - time) / FADE_OUT)
    : time < FADE_IN
    ? time / FADE_IN
    : 1;

const phoneOnly = (styles: ReturnType<typeof css>) => css`
  @media ${PHONE_QUERY} {
    ${styles}
  }
`;

const Card = styled.div`
  width: 100%;
  padding: 22px 24px 16px;
  border-radius: 12px;
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? getNewLookTokens(p.theme).hairline : "transparent"};
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) =>
    p.theme.isDarkMode
      ? "none"
      : "0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 28px rgba(16, 24, 40, 0.07)"};
  ${phoneOnly(css`
    padding: 16px;
  `)}
  /* A phone's walkthrough: the example runs edge to edge, a band between
     hairlines rather than a floating card. */
  @media (max-width: ${WALKTHROUGH_PHONE}px) {
    width: auto;
    margin: 0 -${ONBOARDING_PHONE_GUTTER}px;
    padding-left: ${ONBOARDING_PHONE_GUTTER}px;
    padding-right: ${ONBOARDING_PHONE_GUTTER}px;
    border-width: 1px 0;
    border-color: ${(p) => getNewLookTokens(p.theme).hairline};
    border-radius: 0;
    box-shadow: none;
    /* A soft stage, so the example reads as the page's focus (light; dark
       keeps the card). The drawing's own card colour (fades, halos, chips)
       follows it. */
    ${(p) =>
      !p.theme.isDarkMode &&
      css`
        --viz-card: ${getNewLookTokens(p.theme).well};
        background: ${getNewLookTokens(p.theme).well};
      `}
  }
`;
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  ${phoneOnly(css`
    flex-direction: column;
    gap: 12px;
  `)}
  /* A phone's walkthrough: the figures beside the pair, one block. */
  @media (max-width: ${WALKTHROUGH_PHONE}px) {
    flex-direction: row;
    gap: 12px;
  }
`;
const Pair = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
`;
const Coin = styled.span`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
`;
const PairName = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  min-height: 26px;
  font-size: 15px;
  font-weight: 600;
  color: var(--viz-ink);
`;
const StatusWords = styled.span`
  display: inline-grid;
  > span {
    grid-area: 1 / 1;
  }
  > span[aria-hidden] {
    visibility: hidden;
  }
`;
const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--viz-up);
  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 0 3px var(--viz-up-soft);
  }
  &[data-closed] {
    color: var(--viz-ink-3);
    i {
      box-shadow: none;
    }
  }
`;
const PairMeta = styled.div`
  margin-top: 1px;
  font-size: 12.5px;
  color: var(--viz-ink-3);
  > span {
    white-space: nowrap;
  }
  @media (max-width: ${WALKTHROUGH_PHONE}px) {
    margin-top: 4px;
  }
`;
const Stats = styled.div`
  display: flex;
  gap: 28px;
  text-align: right;
  ${phoneOnly(css`
    gap: 24px;
    padding-left: 36px;
    text-align: left;
  `)}
  @media (max-width: ${WALKTHROUGH_PHONE}px) {
    flex: none;
    gap: 14px;
    padding-left: 0;
    text-align: right;
  }
`;
const StatLabel = styled.div`
  font-size: 12px;
  color: var(--viz-ink-3);
  white-space: nowrap;
`;
const StatValue = styled.div`
  margin-top: 2px;
  font-size: 19px;
  line-height: 24px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--viz-ink);
  /* The value over its widest one (hidden), in one cell. */
  display: grid;
  > span {
    grid-area: 1 / 1;
  }
  > span[aria-hidden] {
    visibility: hidden;
  }
  > [data-positive] {
    color: var(--viz-up);
  }
  ${phoneOnly(css`
    font-size: 17px;
  `)}
  @media (max-width: ${WALKTHROUGH_PHONE}px) {
    font-size: 16px;
    line-height: 22px;
  }
`;
const Strip = styled.div`
  margin-top: 16px;
  padding: 10px 12px;
  border: 1px solid var(--viz-line);
  border-radius: 10px;
  ${phoneOnly(css`
    margin-top: 12px;
    padding: 8px 10px;
  `)}
`;
const StripTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--viz-ink);
  svg {
    color: var(--viz-ink-3);
  }
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  ${phoneOnly(css`
    gap: 5px 4px;
    margin-top: 6px;
  `)}
`;
const StripChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 7px;
  border-radius: 6px;
  background: var(--viz-tint);
  font-size: 11.5px;
  color: var(--viz-ink-2);
  b {
    font-weight: 600;
    color: var(--viz-ink);
  }
  ${phoneOnly(css`
    gap: 4px;
    height: 22px;
    padding: 0 6px;
    font-size: 11px;
  `)}
`;
const Tag = styled.span`
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--viz-up-soft);
  font-size: 11px;
  font-weight: 600;
  color: var(--viz-up);
`;
const Tick = styled.span`
  display: inline-grid;
  place-items: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--viz-up-soft);
  color: var(--viz-up);
`;
const Plot = styled.div<{ $height: number }>`
  position: relative;
  height: ${(p) => p.$height}px;
  margin-top: 12px;
  /* A phone's walkthrough: the chart closer to the figures above it. */
  @media (max-width: ${WALKTHROUGH_PHONE}px) {
    margin-top: 0;
  }
  svg {
    display: block;
    overflow: visible;
  }
  svg text {
    font-variant-numeric: tabular-nums;
  }
`;
const Note = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: var(--viz-ink-3);
  opacity: 0.85;
`;
